import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

import { Logger } from '@/lib/logs/logger';
import { createDraftItemId, MediaMapDraftItem } from '@/pages/shared/uploads/pages/pages/utils/mediaMapItem';

const STORAGE_KEY_PREFIX = 'media-map-draft-';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeItemsIds = (items: MediaMapDraftItem[]) => items.map(({ id: _, ...rest }) => rest);

export const useMediaMapDraft = (configurationId: string, hydratedItems: MediaMapDraftItem[]) => {
  const storageKey = `${STORAGE_KEY_PREFIX}${configurationId}`;

  const [mediaMapDraft, setMediaMapDraft] = useState<MediaMapDraftItem[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as MediaMapDraftItem[];
        if (parsed.length === 0 && hydratedItems.length > 0) {
          // A stale empty draft must not hide server items
          localStorage.removeItem(storageKey);
          return hydratedItems;
        }
        return parsed;
      }
    } catch {
      return hydratedItems;
    }
    return hydratedItems;
  });

  const isDraftMediaMap = useMemo(
    () => !isEqual(removeItemsIds(mediaMapDraft), removeItemsIds(hydratedItems)),
    [mediaMapDraft, hydratedItems],
  );

  const storeMediaMapDraft = useCallback(
    (nextItems: MediaMapDraftItem[]) => {
      setMediaMapDraft(nextItems);
      try {
        localStorage.setItem(storageKey, JSON.stringify(nextItems));
      } catch (e) {
        Logger.error(`Failed to store media map draft. Error found: ${e}`);
      }
    },
    [storageKey],
  );

  const addItemToMediaMapDraft = useCallback(
    (item: Omit<MediaMapDraftItem, 'id'>) => {
      storeMediaMapDraft([...mediaMapDraft, { ...item, id: createDraftItemId() }]);
    },
    [mediaMapDraft, storeMediaMapDraft],
  );

  const updateItemInMediaMapDraft = useCallback(
    (id: string, item: Omit<MediaMapDraftItem, 'id'>) => {
      storeMediaMapDraft(mediaMapDraft.map((existing) => (existing.id === id ? { ...item, id } : existing)));
    },
    [mediaMapDraft, storeMediaMapDraft],
  );

  const removeItemFromMediaMapDraft = useCallback(
    (id: string) => {
      storeMediaMapDraft(mediaMapDraft.filter((existing) => existing.id !== id));
    },
    [mediaMapDraft, storeMediaMapDraft],
  );

  const clearMediaMapDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      Logger.error(`Failed to clear media map draft. Error found: ${e}`);
    }
    setMediaMapDraft(hydratedItems);
  }, [storageKey, hydratedItems]);

  return {
    mediaMapDraft,
    isDraftMediaMap,
    addItemToMediaMapDraft,
    updateItemInMediaMapDraft,
    removeItemFromMediaMapDraft,
    clearMediaMapDraft,
  };
};
